import { useState } from 'react';
import { Button, CircularProgress, Paper, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { CreateWeightUnityRequestDTO } from '../../types/dtos/create-weight-unity.dto';

type Props = {
  isSubmitting: boolean;
  onCreate: (weightUnityData: CreateWeightUnityRequestDTO) => Promise<boolean>;
};

const useStyles = makeStyles((theme) => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem 0.75rem',
  },
  createButton: {
    alignSelf: 'flex-end',
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

export function CreateWeightUnityCard({ isSubmitting, onCreate }: Props) {
  const classes = useStyles();

  const [name, setName] = useState('');
  const [abbreviation, setAbbreviation] = useState('');
  const [isPending, setIsPending] = useState(false);

  const trimmedName = name.trim();
  const trimmedAbbreviation = abbreviation.trim();
  const isBusy = isSubmitting || isPending;
  const canCreate = !isBusy && trimmedName !== '' && trimmedAbbreviation !== '';

  const handleCreate = async () => {
    if (!canCreate) return;

    setIsPending(true);
    try {
      const isCreated = await onCreate({ name: trimmedName, abbreviation: trimmedAbbreviation });
      if (!isCreated) return;

      setName('');
      setAbbreviation('');
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Paper className={classes.card}>
      <TextField
        size="small"
        variant="outlined"
        fullWidth
        id="create-weight-unity-name"
        label="Nom de l'unité"
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      <TextField
        size="small"
        variant="outlined"
        fullWidth
        id="create-weight-unity-abbreviation"
        label="Abréviation"
        value={abbreviation}
        helperText="Affichée après la quantité : 500 g, 1 kg."
        onChange={(event) => setAbbreviation(event.target.value)}
      />
      <Button
        size="small"
        variant="contained"
        className={classes.createButton}
        disabled={!canCreate}
        startIcon={isBusy ? <CircularProgress size={16} /> : null}
        onClick={handleCreate}
      >
        Créer
      </Button>
    </Paper>
  );
}
